import React, { useEffect, useState } from 'react';
import { Form, Upload, message } from 'antd';
import { CloudUploadOutlined, LoadingOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { publicFileUpload } from '../../../../utils/storageUtils';
import BannerCropModal from './BannerCropModal';

export const BANNER_WIDTH = 750;
export const BANNER_HEIGHT = 300;
export const BANNER_MAX_SIZE_MB = 2;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const UPLOAD_TAG = 'driver-activity';
const ERROR_BORDER = '#F0453A';

interface BannerUploadProps {
    /** objectKey（banner_s3_key），由 antd Form.Item 注入 */
    value?: string;
    onChange?: (objectKey: string) => void;
    /** 编辑态已有 Banner 的可访问 URL（详情 bannerUrl） */
    previewUrl?: string | null;
    disabled?: boolean;
    /** antd Form.Item 注入的字段 id，供 scrollToField 定位 */
    id?: string;
}

/** 读图片像素尺寸，读不出来按不合规处理 */
function readImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('image load failed'));
        };
        img.src = url;
    });
}

/**
 * 活动 Banner：420×120 虚线框（原型 .upload）。只拒绝「非 jpg/png」和「原图 > 2MB」；
 * 已是 750×300 的直接上传，其它尺寸先进裁剪弹窗（固定 2.5:1）导出 750×300 png 再上传。
 * 上传走 publicFileUpload(file, 'driver-activity') 公开桶，表单只存 objectKey。
 * 红框只在校验失败且还没上传时显示，上传成功后不红。
 */
const BannerUpload: React.FC<BannerUploadProps> = ({ value, onChange, previewUrl, disabled, id }) => {
    const { t } = useTranslation();
    const { status } = Form.Item.useStatus();
    const [localPreview, setLocalPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [cropFile, setCropFile] = useState<File | null>(null);

    useEffect(() => () => {
        if (localPreview) URL.revokeObjectURL(localPreview);
    }, [localPreview]);

    const preview = localPreview ?? (value ? previewUrl ?? null : null);
    const showError = status === 'error' && !value;

    const upload = async (file: File) => {
        setUploading(true);
        try {
            const { objectKey } = await publicFileUpload(file, UPLOAD_TAG);
            // 旧的 object URL 由上面的 effect cleanup 回收；updater 里不做副作用（StrictMode 会跑两次）
            setLocalPreview(URL.createObjectURL(file));
            onChange?.(objectKey);
            message.success(t('driverOps.activity.banner.uploadSuccess'));
        } catch (e) {
            console.error('Banner upload failed', e);
            message.error(t('driverOps.activity.banner.uploadFailed'));
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload: UploadProps['beforeUpload'] = async (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            message.error(t('driverOps.activity.banner.typeInvalid'));
            return Upload.LIST_IGNORE;
        }
        if (file.size > BANNER_MAX_SIZE_MB * 1024 * 1024) {
            message.error(t('driverOps.activity.banner.sizeInvalid', { max: BANNER_MAX_SIZE_MB }));
            return Upload.LIST_IGNORE;
        }
        let size: { width: number; height: number };
        try {
            size = await readImageSize(file);
        } catch (e) {
            message.error(t('driverOps.activity.banner.typeInvalid'));
            return Upload.LIST_IGNORE;
        }
        if (size.width === BANNER_WIDTH && size.height === BANNER_HEIGHT) {
            await upload(file);
        } else {
            setCropFile(file);
        }
        // 已自行处理，阻止 antd 再发请求
        return false;
    };

    const handleCropped = (file: File) => {
        setCropFile(null);
        void upload(file);
    };

    return (
        <div id={id}>
            <Upload.Dragger
                accept=".jpg,.jpeg,.png"
                multiple={false}
                showUploadList={false}
                beforeUpload={beforeUpload}
                disabled={disabled || uploading}
                style={{ width: 420, padding: 0, borderColor: showError ? ERROR_BORDER : undefined }}
                data-testid="banner-dragger"
            >
                {preview ? (
                    <div className="flex flex-col items-center justify-center w-full">
                        {/* 宽度撑满 Dragger 的内容区（外框 420 减去 antd 16px 内边距），写死 420 会向右溢出导致不居中 */}
                        <img src={preview} alt="banner" className="object-cover rounded-md w-full aspect-[2.5]" />
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-[6px] text-[14px] text-[#1F1F1F] w-full" style={{ height: 120 }}>
                        {uploading ? (
                            <LoadingOutlined style={{ fontSize: 28, color: '#722ed1' }} />
                        ) : (
                            <CloudUploadOutlined style={{ fontSize: 28, color: '#722ed1' }} />
                        )}
                        <div>{t('driverOps.activity.banner.dragHint')}</div>
                        <div className="text-[12.5px] text-[#8C8C8C]">
                            {t('driverOps.activity.banner.specHint', { width: BANNER_WIDTH, height: BANNER_HEIGHT, max: BANNER_MAX_SIZE_MB })}
                        </div>
                    </div>
                )}
            </Upload.Dragger>
            {preview && !disabled && (
                <div className="text-[12.5px] text-[#8C8C8C] mt-[6px]">
                    {t('driverOps.activity.banner.reuploadHint', { width: BANNER_WIDTH, height: BANNER_HEIGHT, max: BANNER_MAX_SIZE_MB })}
                </div>
            )}
            <BannerCropModal
                open={cropFile !== null}
                file={cropFile}
                output={{ width: BANNER_WIDTH, height: BANNER_HEIGHT }}
                onCancel={() => setCropFile(null)}
                onCropped={handleCropped}
            />
        </div>
    );
};

export default BannerUpload;
